<template>
  <div class="young-user-card" :class="{ 'card-pending': isPending }" @click="handleClick">
    <!-- Disconnect Button -->
    <button class="btn-card-menu" @click.stop="handleDisconnect" title="Déconnecter ce jeune">
      <svg class="delete-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>

    <!-- Pending Overlay -->
    <div v-if="isPending" class="pending-overlay">
      <div class="pending-icon">🔒</div>
      <h4 class="pending-title">En attente d'acceptation</h4>
      <p class="pending-text">Le jeune doit accepter votre invitation pour que vous puissiez accéder à son profil.</p>
      <p class="pending-text2">Il pourra trouver son code dans son espace Profil.</p>
    </div>

    <div class="card-header">
      <div class="avatar-container">
        <div class="avatar" v-if="!youngUser.profilePicture">
          {{ initials }}
        </div>
        <img v-else :src="youngUser.profilePicture" :alt="fullName" class="avatar-img" />
        <span v-if="isPending" class="pending-badge">⏳</span>
        <span
          v-else-if="youngUser.otherAccompanists && youngUser.otherAccompanists.length > 0"
          class="accompanists-count-badge"
          :title="otherAccompanistsTooltip"
        >
          +{{ youngUser.otherAccompanists.length }}
        </span>
      </div>

      <div class="user-info">
        <h3 class="user-name">{{ fullName }}</h3>
        <p class="user-age" v-if="!isPending">{{ youngUser.age }} ans</p>
        <p class="user-status" v-else>Invitation en attente</p>
        <div class="user-meta-section" v-if="!isPending">
          <p class="last-activity">
            <span class="activity-dot" :class="activityStatus"></span>
            Dernière activité: {{ lastActivityFormatted }}
          </p>
          <div v-if="youngUser.otherAccompanists && youngUser.otherAccompanists.length > 0" class="other-accompanists">
            <span class="accompanists-label">Aussi suivi par:</span>
            <div class="accompanists-tags">
              <span
                v-for="acc in youngUser.otherAccompanists"
                :key="acc.id"
                class="accompanist-tag"
                :class="`type-${acc.type}`"
                :title="acc.name"
              >
                {{ acc.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card-stats">
      <div class="stat-box">
        <div class="stat-icon">🎮</div>
        <div class="stat-content">
          <span class="stat-value">{{ youngUser.gamesCompletion }}%</span>
          <span class="stat-label">Jeux complétés</span>
        </div>
      </div>

      <div class="stat-box">
        <div class="stat-icon">📝</div>
        <div class="stat-content">
          <span class="stat-value">{{ youngUser.observationsCount }}</span>
          <span class="stat-label">Observations</span>
        </div>
      </div>

      <div class="stat-box">
        <div class="stat-icon">🏆</div>
        <div class="stat-content">
          <span class="stat-value">{{ youngUser.badgesCount }}</span>
          <span class="stat-label">Badges</span>
        </div>
      </div>
    </div>

    <div class="card-footer">
      <button class="btn-view-profile" v-if="!isPending" @click.stop="handleClick">
        Voir le profil
        <span class="arrow">→</span>
      </button>
      <button class="btn-waiting" v-else disabled>
        <span class="waiting-icon">⏳</span>
        En attente
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'YoungUserCard',
  props: {
    youngUser: {
      type: Object,
      required: true
    }
  },
  computed: {
    isPending() {
      return this.youngUser.invitationStatus === 'pending';
    },
    fullName() {
      return `${this.youngUser.firstName}`;
    },
    initials() {
      return `${this.youngUser.firstName?.[0] || ''}${this.youngUser.lastName?.[0] || ''}`.toUpperCase();
    },
    otherAccompanistsTooltip() {
      if (!this.youngUser.otherAccompanists || this.youngUser.otherAccompanists.length === 0) {
        return '';
      }
      const labels = {
        parent: 'Parent',
        educateur: 'Éducateur',
        therapeute: 'Thérapeute',
        proche: 'Proche'
      };
      return this.youngUser.otherAccompanists
        .map(acc => `${acc.name} (${labels[acc.type] || acc.type})`)
        .join(', ');
    },
    lastActivityFormatted() {
      const date = new Date(this.youngUser.lastActivity);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Aujourd\'hui';
      if (diffDays === 1) return 'Hier';
      if (diffDays < 7) return `Il y a ${diffDays} jours`;
      return date.toLocaleDateString('fr-FR');
    },
    activityStatus() {
      const date = new Date(this.youngUser.lastActivity);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'active';
      if (diffDays <= 3) return 'recent';
      return 'inactive';
    }
  },
  methods: {
    handleClick() {
      if (!this.isPending) {
        this.$emit('click', this.youngUser);
      }
    },
    handleDisconnect() {
      this.$emit('disconnect', this.youngUser);
    }
  }
};
</script>

<style scoped>
.young-user-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
  position: relative;
}

.young-user-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-4px);
  border-color: #FF9800;
}

.young-user-card.card-pending {
  background: #F9FAFB;
  cursor: default;
  opacity: 0.95;
}

.young-user-card.card-pending:hover {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transform: none;
  border-color: #F59E0B;
}

.pending-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  z-index: 10;
  backdrop-filter: blur(8px);
}

.pending-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  animation: lockShake 2s infinite;
}

@keyframes lockShake {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-5deg); }
  75% { transform: rotate(5deg); }
}

.pending-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.pending-text {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
  line-height: 1.5;
}

.pending-text2 {
  padding-top: 0.5rem;
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;;
  line-height: 1.5;
  font-weight: bold;
}


.pending-badge {
  position: absolute;
  bottom: -4px;
  right: -4px;
  background: #F59E0B;
  color: white;
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  border: 2px solid white;
  animation: pulse 2s infinite;
}

.card-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.avatar-container {
  position: relative;
  flex-shrink: 0;
}

.avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
}

.avatar-img {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
}

.accompanists-count-badge {
  position: absolute;
  bottom: -4px;
  right: -4px;
  background: #FF9800;
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  border: 2px solid white;
  min-width: 28px;
  text-align: center;
  cursor: help;
}

.user-info {
  flex: 1;
}

.user-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.user-age {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0 0 0.5rem 0;
}

.user-status {
  font-size: 0.875rem;
  color: #F59E0B;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
}

.user-meta-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.last-activity {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #9CA3AF;
  margin: 0;
}

.other-accompanists {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.accompanists-label {
  font-size: 0.625rem;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.accompanists-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.accompanist-tag {
  font-size: 0.625rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-weight: 600;
  background: #E5E7EB;
  color: #374151;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.accompanist-tag.type-parent {
  background: #EDE9FE;
  color: #6B21A8;
}

.accompanist-tag.type-educateur {
  background: #D1FAE5;
  color: #065F46;
}

.accompanist-tag.type-therapeute {
  background: #FEF3C7;
  color: #92400E;
}

.activity-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #D1D5DB;
}

.activity-dot.active {
  background: #10B981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);
  animation: pulse 2s infinite;
}

.activity-dot.recent {
  background: #F59E0B;
}

.activity-dot.inactive {
  background: #EF4444;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.card-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.stat-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  background: #F9FAFB;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.stat-box:hover {
  background: #F3F4F6;
  transform: scale(1.05);
}

.stat-icon {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.stat-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

.stat-label {
  font-size: 0.625rem;
  color: #6B7280;
  text-align: center;
  margin-top: 0.25rem;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
}

.btn-view-profile {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

.btn-view-profile:hover {
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.5);
  transform: translateX(4px);
}

.btn-view-profile .arrow {
  font-size: 1rem;
  transition: transform 0.2s ease;
}

.btn-view-profile:hover .arrow {
  transform: translateX(4px);
}

.btn-waiting {
  background: #FEF3C7;
  color: #92400E;
  border: 2px solid #F59E0B;
  border-radius: 8px;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: not-allowed;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.8;
}

.waiting-icon {
  animation: pulse 2s infinite;
}

.btn-card-menu {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: #FEE2E2;
  border: none;
  border-radius: 8px;
  padding: 0.5rem;
  font-size: 1.125rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  width: 28px;
  height: 28px;
}

.btn-card-menu:hover {
  background: #FCA5A5;
  transform: scale(1.1);
}

.delete-icon {
  width: 18px;
  height: 18px;
  color: #DC2626;
}
</style>
